<script setup>
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  title: { type: String, required: true },
  placeholder: { type: String, required: true },
  emptyState: { type: String, required: true },
  addLabel: { type: String, required: true },
  labels: { type: Array, required: true },
  selectedIds: { type: Array, required: true },
});

const emit = defineEmits(['update:selectedIds']);

const selectorValue = ref('');

const availableOptions = computed(() =>
  props.labels
    .filter(label => !props.selectedIds.includes(label.id))
    .map(label => ({ value: label.id, label: label.title }))
);

const addSelectedLabel = () => {
  if (!selectorValue.value) return;
  emit('update:selectedIds', [
    ...props.selectedIds,
    Number(selectorValue.value),
  ]);
  selectorValue.value = '';
};

const removeSelectedLabel = labelId => {
  emit(
    'update:selectedIds',
    props.selectedIds.filter(id => id !== labelId)
  );
};

const labelTitle = labelId => {
  const matchingLabel = props.labels.find(
    label => label.id === Number(labelId)
  );
  return matchingLabel?.title ?? String(labelId);
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <p class="text-sm font-medium text-n-slate-12">{{ title }}</p>
    <div class="flex items-center gap-2">
      <ComboBox
        v-model="selectorValue"
        :options="availableOptions"
        :placeholder="placeholder"
        :empty-state="emptyState"
        class="flex-1"
      />
      <Button
        variant="outline"
        icon="i-lucide-plus"
        :label="addLabel"
        :disabled="!selectorValue"
        @click="addSelectedLabel"
      />
    </div>
    <ul v-if="selectedIds.length" class="flex flex-wrap gap-1.5 mt-1">
      <li
        v-for="labelId in selectedIds"
        :key="labelId"
        class="inline-flex items-center gap-1 px-2 py-1 rounded bg-n-alpha-2 text-xs text-n-slate-11"
      >
        <span>{{ labelTitle(labelId) }}</span>
        <button
          type="button"
          class="hover:text-n-ruby-9"
          @click="removeSelectedLabel(labelId)"
        >
          <Icon icon="i-lucide-x" class="size-3" />
        </button>
      </li>
    </ul>
  </div>
</template>
